import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth, formatError } from "@/context/AuthContext";

export default function Auth() {
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [form, setForm] = useState({ name: "", surname: "", email: "", phone: "", birthday: "", password: "", marketing_consent: false });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const ret = params.get("return") || "/kima-id";

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email, origin_url: window.location.origin });
        toast.success("Se l'email è registrata, riceverai un link per reimpostare la password.");
        setMode("login");
        return;
      }
      const u = mode === "login" ? await login(form.email, form.password) : await register(form);
      toast.success(mode === "login" ? "Bentornata!" : "Account creato!");
      nav(u.role === "client" ? ret : "/admin");
    } catch (e2) {
      setErr(formatError(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  const inp = "w-full border-b border-espresso/20 bg-transparent py-3 focus:outline-none focus:border-espresso transition-colors text-espresso";
  const titles = { login: "Accedi", register: "Crea il tuo KIMA ID", forgot: "Recupera password" };
  const overlines = { login: "Bentornata", register: "Unisciti a KIMA", forgot: "Nessun problema" };

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2">
      <div className="hidden lg:block hover-zoom"><img src="https://customer-assets-lqy194kg.emergentagent.net/job_kima-id/artifacts/5nyg6j7o_94c3e1f6-9acf-4a62-a36f-5562327f1bed.png" alt="Kilian Mallao · KIMA Nails" className="w-full h-full object-cover object-top" /></div>
      <div className="flex items-center justify-center px-6 py-16">
        <form onSubmit={submit} className="w-full max-w-sm animate-fade-up">
          <span className="kima-overline text-burgundy">{overlines[mode]}</span>
          <h1 className="font-serif text-4xl text-espresso mt-3 mb-8">{titles[mode]}</h1>
          {err && <div className="bg-kimaerror/10 text-kimaerror text-sm p-3 rounded-sm mb-4" data-testid="auth-error">{err}</div>}
          {mode === "forgot" ? (
            <div className="space-y-5">
              <p className="text-sm text-espresso/60">Inserisci l'email del tuo KIMA ID: ti invieremo un link per creare una nuova password.</p>
              <input className={inp} type="email" placeholder="Email" required data-testid="forgot-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <button disabled={loading} data-testid="forgot-submit" className="w-full bg-espresso text-ivory py-4 rounded-sm hover:bg-burgundy transition-colors disabled:opacity-50">{loading ? "Attendere…" : "Invia link di recupero"}</button>
              <button type="button" onClick={() => { setMode("login"); setErr(""); }} data-testid="forgot-back" className="w-full text-center text-sm text-taupe hover:text-espresso transition-colors">← Torna all'accesso</button>
            </div>
          ) : (
          <>
          <div className="space-y-5">
            {mode === "register" && (<>
              <input className={inp} placeholder="Nome" required data-testid="auth-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <input className={inp} placeholder="Cognome" data-testid="auth-surname" value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })} />
              <input className={inp} placeholder="Telefono" data-testid="auth-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <div><label className="text-xs text-espresso/50">Data di nascita (facoltativa)</label><input className={inp} type="date" data-testid="auth-birthday" value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} /></div>
            </>)}
            <input className={inp} type="email" placeholder="Email" required data-testid="auth-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input className={inp} type="password" placeholder="Password" required data-testid="auth-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            {mode === "login" && (
              <button type="button" onClick={() => { setMode("forgot"); setErr(""); }} data-testid="forgot-link" className="text-xs text-taupe hover:text-burgundy transition-colors">Password dimenticata?</button>
            )}
            {mode === "register" && (
              <label className="flex items-start gap-2 text-xs text-espresso/60"><input type="checkbox" checked={form.marketing_consent} onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })} data-testid="auth-marketing" className="mt-1" /> Desidero ricevere comunicazioni marketing (facoltativo)</label>
            )}
          </div>
          <button disabled={loading} data-testid="auth-submit" className="w-full bg-espresso text-ivory py-4 rounded-sm hover:bg-burgundy transition-colors mt-8 disabled:opacity-50">{loading ? "Attendere…" : mode === "login" ? "Accedi" : "Registrati"}</button>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px bg-espresso/15 flex-1" />
            <span className="text-xs text-taupe">oppure</span>
            <div className="h-px bg-espresso/15 flex-1" />
          </div>
          <button type="button" onClick={loginWithGoogle} data-testid="google-login-btn" className="w-full flex items-center justify-center gap-3 border border-espresso/20 py-3.5 rounded-sm hover:border-espresso hover:bg-offwhite transition-colors text-sm text-espresso">
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
            Continua con Google
          </button>
          <button type="button" onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }} data-testid="auth-toggle" className="w-full text-center text-sm text-taupe hover:text-espresso transition-colors mt-5">
            {mode === "login" ? "Non hai un account? Registrati" : "Hai già un account? Accedi"}
          </button>
          </>
          )}
        </form>
      </div>
    </div>
  );
}
